import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosRequestConfig,
  AxiosHeaders,
  AxiosHeaderValue,
} from "axios";
import type {
  ApiInstanceConfig,
  ApiInstancesMap,
  ResponseInterceptorConfig,
  CustomRequestOptions,
  RequestInterceptorConfig,
} from "api";

// Interface para contexto de erro
interface ErrorContext {
  status?: number;
  message: string;
  url?: string;
  method?: string;
  response?: unknown;
}

/**
 * Cria uma instância básica do Axios com configurações padrão.
 * @param config Configuração da instância.
 * @returns Nova instância do Axios.
 */
export function createAxiosInstance(config: ApiInstanceConfig): AxiosInstance {
  return axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout || 30_000,
    headers: new AxiosHeaders({
      "Content-Type": "application/json",
      ...config.headers,
    }),
  });
}

/**
 * Configura o interceptor de requisição para adicionar headers e gerenciar autenticação.
 * @param instance Instância do Axios.
 * @param cfg Configuração do interceptor de requisição.
 */
export function setupRequestInterceptor(
  instance: AxiosInstance,
  cfg: RequestInterceptorConfig
): void {
  instance.interceptors.request.use(
    async (request) => {
      // Verificação de segurança para ambiente de build
      const isBuildTime = typeof window === 'undefined';
      
      if (!(request.headers instanceof AxiosHeaders)) {
        request.headers = new AxiosHeaders(request.headers);
      }

      if (cfg.staticHeaders) {
        const staticHeadersToAdd = cfg.staticHeaders();
        for (const key in staticHeadersToAdd) {
          request.headers.set(key, staticHeadersToAdd[key]);
        }
      }

      if ((request as CustomRequestOptions).skipAuth) {
        return request;
      }

      const forceToken = (request as CustomRequestOptions).forceToken;
      if (forceToken) {
        request.headers.set("Authorization", `Bearer ${forceToken}`);
        return request;
      }

      // Durante build time, pula autenticação para evitar erros
      if (!isBuildTime) {
        const currentBase = request.baseURL || "";
        const needsAuth = cfg.authRequiredUrls.some((u) =>
          currentBase.includes(u)
        );

        if (needsAuth) {
          const token = await cfg.tokenProvider();
          if (token) {
            request.headers.set("Authorization", `Bearer ${token}`);
          }
        }

        if (cfg.tenantIdProvider && request.headers) {
          const tenantId = await cfg.tenantIdProvider();
          if (tenantId && tenantId !== "default") {
            request.headers.set("TenantID", tenantId);
          }
        }
      }

      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

/**
 * Configura o interceptor de resposta para lidar com sucessos e erros.
 * @param instance Instância do Axios.
 * @param cfg Configuração do interceptor de respostas.
 */
export function setupResponseInterceptor(
  instance: AxiosInstance,
  cfg: ResponseInterceptorConfig
): void {
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      // Tratamento de erro 403 - tenta com próxima filial
      if (error.response?.status === 403) {
        // Verifica se é uma requisição para API de produção
        const isProductionRequest =
          error.config?.baseURL?.includes("/api/production") ||
          error.config?.url?.includes("/api/production");

        if (isProductionRequest && cfg.tokenProvider) {
          // Verificação de segurança para ambiente de build
          const isBuildTime = typeof window === 'undefined';
          
          if (!isBuildTime) {
            try {
              // Importa dinamicamente para evitar dependência circular
              const { getNextTenantId } = await import("@/_nucleo/api/client");

              // Tenta com a próxima filial
              const nextTenantId = await getNextTenantId();

              if (nextTenantId && nextTenantId !== "default") {
                // Atualiza o store com o novo tenantId
                const { useUserStore } = await import(
                  "@/_nucleo/store/_auth.store"
                );
                const { setUserData } = useUserStore.getState();
                const auth = useUserStore.getState().auth;

                if (auth) {
                  setUserData({
                    auth: {
                      ...auth,
                      tenantId: nextTenantId,
                    },
                  });
                }

                // Retry da requisição com o novo tenantId
                const retryConfig = {
                  ...error.config,
                  headers: {
                    ...error.config?.headers,
                    TenantID: nextTenantId,
                  },
                };

                return instance.request(retryConfig);
              }
            } catch (retryError) {
              // Se falhar o retry, continua com o erro original
              console.warn("Falha ao tentar com próxima filial:", retryError);
            }
          }
        }

        cfg.onAuthError?.(error);
      } else if (error.response?.status === 401) {
        cfg.onAuthError?.(error);
      } else {
        cfg.onError?.(error);
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Extrai um contexto de erro estruturado de um `AxiosError`.
 * @param error O erro Axios.
 * @returns Contexto de erro estruturado.
 */
export function extractErrorContext(error: AxiosError): ErrorContext {
  return {
    status: error.response?.status,
    message: error.message || "Erro desconhecido",
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    response: error.response?.data,
  };
}

/**
 * Verifica se um erro está relacionado à autenticação (status 401 ou 403).
 * @param context Contexto do erro.
 * @returns `true` se for um erro de autenticação, `false` caso contrário.
 */
export function isAuthError(context: ErrorContext): boolean {
  return context.status === 401 || context.status === 403;
}

/**
 * Identifica URLs que requerem autenticação a partir de um mapa de configurações de API.
 * @param apiConfigs Mapa de configurações de API.
 * @returns Array de URLs que requerem autenticação.
 */
export function extractAuthRequiredUrls(apiConfigs: ApiInstancesMap): string[] {
  return Object.values(apiConfigs)
    .filter((config) => config.requiresAuth)
    .map((config) => config.baseURL);
}

/**
 * Valida se uma string é uma URL bem formada.
 * @param url A string URL a ser validada.
 * @returns `true` se a URL é válida, `false` caso contrário.
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Normaliza uma URL, removendo barras duplas e garantindo um formato consistente.
 * @param url A URL a ser normalizada.
 * @returns A URL normalizada.
 */
export function normalizeUrl(url: string): string {
  return url.replace(/([^:]\/)\/+/g, "$1").replace(/\/$/, "");
}

/**
 * Cria uma configuração padrão para uma instância de API.
 * @param baseURL URL base da API.
 * @param requiresAuth Se a instância requer autenticação (padrão: `false`).
 * @param options Opções adicionais para a configuração.
 * @returns Configuração da instância de API.
 */
export function createDefaultApiConfig(
  baseURL: string,
  requiresAuth: boolean = false,
  options: Partial<ApiInstanceConfig> = {}
): ApiInstanceConfig {
  return {
    baseURL: normalizeUrl(baseURL),
    requiresAuth,
    timeout: 240000, // 240 segundos (4 minutos)
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    ...options,
  };
}

/**
 * Mescla configurações de requisição padrão com configurações customizadas.
 * @param defaultConfig Configuração padrão do Axios.
 * @param customConfig Opções de requisição customizadas.
 * @returns Configuração de requisição mesclada.
 */
export function mergeRequestConfig(
  defaultConfig: AxiosRequestConfig,
  customConfig: CustomRequestOptions = {}
): AxiosRequestConfig {
  const mergedHeaders: Record<string, AxiosHeaderValue> = {};

  if (defaultConfig.headers) {
    for (const key in defaultConfig.headers) {
      const value = (
        defaultConfig.headers as Record<string, AxiosHeaderValue | undefined>
      )[key];
      if (value !== undefined) {
        mergedHeaders[key] = value;
      }
    }
  }

  if (customConfig.headers) {
    for (const key in customConfig.headers) {
      const value = (
        customConfig.headers as Record<string, AxiosHeaderValue | undefined>
      )[key];
      if (value !== undefined) {
        mergedHeaders[key] = value;
      }
    }
  }

  return {
    ...defaultConfig,
    ...customConfig,
    headers: new AxiosHeaders(mergedHeaders),
  };
}
